import React, { useMemo } from 'react';
import { Box, Grid, ListItemField } from '@common-fe/common-fe';

import useRadioCardLayout from '../hooks/useRadioCardLayout';

import CheckCardAddressRow from './CheckCardAddressRow';
import CheckCardItem from './CheckCardItem';
import CheckCardSelector from './CheckCardSelector';

const SENT_TO_FIELD_KEY = 'checkSentTo';
const PAY_TO_FIELD_KEY = 'payTo';
const ADDRESS_ROW_FIELD_KEYS = [SENT_TO_FIELD_KEY, PAY_TO_FIELD_KEY];

interface Props {
  testId?: string;
  itemKey?: string;
  itemFields?: ListItemField[];
  isSelected?: boolean;
  onSelect?: (value?: string) => void;
  children?: React.ReactNode;
  isEditMode?: boolean;
}

const CheckRadioButtonCard: React.FC<Props> = ({
  testId, itemKey, isSelected, onSelect, itemFields, isEditMode, children,
}) => {
  const gridFields = useMemo(
    () => (itemFields || []).filter((field) => !ADDRESS_ROW_FIELD_KEYS.includes(field.key ?? '')),
    [itemFields]
  );
  const sentToField = useMemo(
    () => (itemFields || []).find((field) => field.key === SENT_TO_FIELD_KEY),
    [itemFields]
  );
  const payToField = useMemo(
    () => (itemFields || []).find((field) => field.key === PAY_TO_FIELD_KEY),
    [itemFields]
  );

  const {
    isExpanded,
    visibleGridFields,
    gridColumns,
    handleToggleExpand,
    handleSelect,
  } = useRadioCardLayout({ itemKey, gridFields, isEditMode, onSelect });

  return (
    <Box
      data-testid={`${testId}_radio-button-card`}
      background="canvas"
      round="container1Round"
      pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
      border={{ size: '1px', color: 'border2' }}
    >
      <Box direction="row">
        {isEditMode && (
          <CheckCardSelector
            testId={testId}
            isSelected={isSelected}
            isExpanded={isExpanded}
            onSelect={handleSelect}
            onToggleExpand={handleToggleExpand}
          />
        )}

        <Box direction="column" width="100%">
          <Grid
            width="100%"
            columns={gridColumns}
            gap={{ column: 'spacing12', row: 'spacing8' }}
          >
            {visibleGridFields?.map((cardItem) => (
              <CheckCardItem
                testId={`${testId}_${cardItem.key}`}
                key={cardItem.key}
                title={cardItem.title}
                value={cardItem.value}
                node={cardItem.node}
                style={cardItem.style}
              />
            ))}
          </Grid>

          {isExpanded && (
            <CheckCardAddressRow
              testId={testId}
              sentToField={sentToField}
              payToField={payToField}
            />
          )}
        </Box>
      </Box>
      {children}
    </Box>
  );
};

export default CheckRadioButtonCard;
